import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Terminal } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { serviceImage } from "../hero/productImages";
import { serviceSlug } from "../../data/serviceDetails";

const reveal = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" } };

export const HowItWorks = ({ steps }) => (
  <section className="py-20 md:py-24 bg-[#F8FAFC]" data-testid="detail-steps">
    <div className="pp-container">
      <motion.h2 {...reveal} className="text-3xl md:text-4xl font-extrabold text-[#0D1B2A] tracking-tight">How it <span className="pp-grad-text">works</span></motion.h2>
      <div className="relative mt-14 grid md:grid-cols-4 gap-8">
        <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-[#06B6D4] via-[#0EA5E9] to-[#10B981] opacity-40" />
        {steps.map((st, i) => (
          <motion.div key={st.t} {...reveal} transition={{ delay: i * 0.12 }} className="relative flex md:flex-col items-start md:items-center gap-4 md:text-center" data-testid={`detail-step-${i}`}>
            <div className="relative shrink-0 w-14 h-14 rounded-2xl bg-[#050C17] text-[#67E8F9] font-extrabold text-lg flex items-center justify-center shadow-[0_0_0_6px_#F8FAFC,0_15px_30px_-10px_rgba(6,182,212,0.6)]">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div>
              <div className="font-bold text-[#0D1B2A]">{st.t}</div>
              <div className="text-sm text-[#64748B] mt-1">{st.d}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const UseCases = ({ items, label }) => (
  <section className="py-20 md:py-24 bg-[#050C17] relative overflow-hidden" data-testid="detail-usecases">
    <div className="absolute inset-0 pp-hero-grid opacity-60" />
    <div className="relative pp-container">
      <motion.div {...reveal}>
        <span className="text-[#10B981] font-semibold uppercase tracking-wider text-sm">Built for</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-white tracking-tight">Who uses {label}</h2>
      </motion.div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((u, i) => (
          <motion.div key={u.t} {...reveal} transition={{ delay: i * 0.08 }} className="group rounded-3xl p-6 bg-white/[0.04] border border-white/10 hover:border-[#06B6D4]/50 hover:bg-white/[0.07] transition-colors" data-testid={`detail-usecase-${i}`}>
            <div className="text-5xl font-extrabold text-white/10 group-hover:text-[#06B6D4]/40 transition-colors">{String(i + 1).padStart(2, "0")}</div>
            <div className="mt-4 font-bold text-white">{u.t}</div>
            <div className="text-sm text-slate-400 mt-1">{u.d}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Json = ({ obj }) => (
  <pre className="text-[12px] md:text-[13px] leading-relaxed whitespace-pre-wrap break-all">
    {JSON.stringify(obj, null, 2).split("\n").map((ln, i) => (
      <div key={i} dangerouslySetInnerHTML={{ __html: ln.replace(/("[^"]+")(:)/g, '<span style="color:#67E8F9">$1</span>$2').replace(/: ("[^"]*")/g, ': <span style="color:#6EE7B7">$1</span>') }} />
    ))}
  </pre>
);

export const ApiPreview = ({ api }) => (
  <section className="py-20 md:py-24 bg-white" data-testid="detail-api">
    <div className="pp-container grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-center">
      <motion.div {...reveal}>
        <span className="text-[#06B6D4] font-semibold uppercase tracking-wider text-sm">For developers</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0D1B2A] tracking-tight">Integrate in an <span className="pp-grad-text">afternoon</span></h2>
        <p className="mt-4 text-[#475569]">Clean REST APIs, sandbox keys, webhooks and idempotent requests — so your team ships fast and sleeps well.</p>
      </motion.div>
      <motion.div {...reveal} className="rounded-3xl bg-[#050C17] border border-[#06B6D4]/20 overflow-hidden shadow-[0_40px_80px_-40px_rgba(13,27,42,0.7)]">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
          <span className="w-3 h-3 rounded-full bg-red-400/80" /><span className="w-3 h-3 rounded-full bg-yellow-400/80" /><span className="w-3 h-3 rounded-full bg-green-400/80" />
          <span className="ml-3 text-xs text-slate-400 flex items-center gap-1.5"><Terminal size={12} /> <b className="text-[#10B981]">{api.method}</b> {api.path}</span>
        </div>
        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10 text-slate-200">
          <div className="p-5"><div className="text-[10px] font-bold tracking-widest text-slate-500 mb-2">REQUEST</div><Json obj={api.body} /></div>
          <div className="p-5"><div className="text-[10px] font-bold tracking-widest text-slate-500 mb-2">RESPONSE · 200</div><Json obj={api.res} /></div>
        </div>
      </motion.div>
    </div>
  </section>
);

export const DetailFAQ = ({ faqs }) => (
  <section className="py-20 md:py-24 bg-[#F8FAFC]" data-testid="detail-faq">
    <div className="pp-container grid lg:grid-cols-[0.7fr_1.3fr] gap-10">
      <motion.h2 {...reveal} className="text-3xl md:text-4xl font-extrabold text-[#0D1B2A] tracking-tight">Questions, <span className="pp-grad-text">answered</span></motion.h2>
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`f${i}`} className="bg-white border border-slate-100 rounded-2xl px-5" data-testid={`detail-faq-${i}`}>
            <AccordionTrigger className="text-left font-semibold text-[#0D1B2A] hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-[#475569]">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const RelatedServices = ({ items }) => {
  const navigate = useNavigate();
  if (!items.length) return null;
  return (
    <section className="py-20 bg-white" data-testid="detail-related">
      <div className="pp-container">
        <h2 className="text-2xl md:text-3xl font-extrabold text-[#0D1B2A] tracking-tight">Explore more solutions</h2>
        <div className="mt-8 grid sm:grid-cols-3 gap-5">
          {items.map((r) => (
            <button key={r.id} onClick={() => navigate(`/services/${serviceSlug(r)}`)} data-testid={`related-${serviceSlug(r)}`}
              className="group text-left rounded-3xl overflow-hidden border border-slate-100 bg-[#F8FAFC] hover:-translate-y-1 transition-transform">
              <div className="h-36 bg-black overflow-hidden"><img src={serviceImage(r.visual)} alt={r.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /></div>
              <div className="p-5 flex items-center justify-between">
                <div><div className="text-[11px] text-[#64748B]">{r.group}</div><div className="font-bold text-[#0D1B2A]">{r.label}</div></div>
                <ArrowUpRight size={18} className="text-[#0EA5E9] group-hover:rotate-45 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const DetailCTA = ({ label }) => {
  const navigate = useNavigate();
  return (
    <section className="pp-container pb-20">
      <div className="relative overflow-hidden rounded-[36px] p-10 md:p-14 bg-[#050C17]" data-testid="detail-cta">
        <div className="absolute -top-24 -right-10 w-96 h-96 rounded-full blur-[110px] bg-[#06B6D4]/35" />
        <div className="absolute -bottom-24 left-10 w-80 h-80 rounded-full blur-[110px] bg-[#10B981]/25" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-white text-3xl md:text-4xl font-extrabold tracking-tight">Ready to launch {label}?</h2>
            <p className="text-slate-300 mt-2">Create your account in 2 minutes — our team will help you go live.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate("/signup")} data-testid="detail-cta-signup" className="group inline-flex items-center gap-2 text-[#030712] font-semibold px-6 py-3 rounded-full" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
              Create Account <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate("/#cta")} data-testid="detail-cta-contact" className="px-6 py-3 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors">Contact Us</button>
          </div>
        </div>
      </div>
    </section>
  );
};
